import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col, Row, View } from '@amzn/stencil-react-components/layout'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { Text } from '@amzn/stencil-react-components/text'

import { LookAndFeelService, LookAndFeelSummary } from 'src/services/backend/LookAndFeelService'
import { ModuleService } from 'src/services/backend/ModuleService'
import { ActivityEntityService } from 'src/services/EntityServices/ActivityEntityService'
import { ModuleEntityService } from 'src/services/EntityServices/ModuleEntityService'

export interface LookAndFeelGalleryProps {
    /** Item type whose look-and-feels to browse (matches the gallery partition key). */
    itemType: string
    /** Entity id of the module currently being edited; the picked page is added to it. */
    moduleEntityId: string
    /** Optional workflow position to insert at; appends to the end when omitted. */
    pageIndex?: number
    /** Called after a page has been inserted (e.g. to close the picker). */
    onInserted?: () => void
}

type Variant = 'template' | 'real'

const PREVIEW_HEIGHT = 460
const THUMB_WIDTH = 150
const THUMB_HEIGHT = 110
/** Opacity of the download action while an insert is in flight. */
const INSERTING_OPACITY = 0.7
/** Opacity of a disabled nav control. */
const NAV_DISABLED_OPACITY = 0.35

/** Circular translucent nav button pinned to the left/right edge of the preview. */
const navStyle = (side: 'left' | 'right', disabled: boolean): React.CSSProperties => ({
    position: 'absolute',
    top: '50%',
    [side]: 12,
    transform: 'translateY(-50%)',
    zIndex: 5,
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.9)',
    color: '#16191f',
    fontSize: 24,
    lineHeight: '38px',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? NAV_DISABLED_OPACITY : 1,
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
})

/**
 * A "pick a look-and-feel" gallery. A large preview shows the currently selected look-and-feel; a
 * horizontally scrollable strip of thumbnails lets the author browse and select (template
 * placeholders by default, real renders on toggle). Look-and-feels with no image for the current
 * variant are skipped. Hovering the preview reveals a download button that fetches the selected
 * scrubbed page and inserts it as a new page into the module being edited, reusing the app's own
 * activity hydration + duplication so nested items get fresh ids.
 */
export const LookAndFeelGallery = ({
    itemType,
    moduleEntityId,
    pageIndex,
    onInserted,
}: LookAndFeelGalleryProps) => {
    const [items, setItems] = useState<LookAndFeelSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [variant, setVariant] = useState<Variant>('template')
    const [selectedHash, setSelectedHash] = useState<string>()
    const [inserting, setInserting] = useState(false)
    const [hoverPreview, setHoverPreview] = useState(false)
    const selectedThumbRef = useRef<HTMLButtonElement>(null)
    const stripRef = useRef<HTMLDivElement>(null)

    const imageFor = useCallback(
        (it?: LookAndFeelSummary) =>
            it && (variant === 'template' ? it.templateImageUrl : it.realImageUrl),
        [variant]
    )

    // Only show look-and-feels that actually have an image for the current variant; the rest are
    // skipped entirely (no "no image" placeholder).
    const visibleItems = useMemo(() => items.filter((it) => imageFor(it)), [items, imageFor])

    useEffect(() => {
        const abort = new AbortController()
        setLoading(true)
        setError('')
        LookAndFeelService.getItemLookAndFeels(itemType, abort)
            .then((result) => {
                setItems(result)
                const first = result.find((r) => r.templateImageUrl || r.realImageUrl)
                setSelectedHash(first?.signatureHash)
            })
            .catch((e: unknown) => {
                if (!abort.signal.aborted) {
                    console.error('Failed to load look-and-feels', e)
                    setError('Failed to load look-and-feels')
                }
            })
            .finally(() => {
                if (!abort.signal.aborted) {
                    setLoading(false)
                }
            })
        return () => abort.abort()
    }, [itemType])

    // If the current selection isn't visible in this variant, snap to the first visible one.
    useEffect(() => {
        if (visibleItems.length === 0) {
            return
        }
        if (!visibleItems.some((it) => it.signatureHash === selectedHash)) {
            setSelectedHash(visibleItems[0].signatureHash)
        }
    }, [visibleItems, selectedHash])

    const selectedIndex = Math.max(
        0,
        visibleItems.findIndex((it) => it.signatureHash === selectedHash)
    )
    const selected = visibleItems[selectedIndex]
    const selectedHashResolved = selected?.signatureHash
    const selectedUrl = imageFor(selected)

    // Keep the selected thumbnail centered in the (horizontally scrollable) strip as the selection
    // moves, so navigating never pushes it out of view.
    useEffect(() => {
        selectedThumbRef.current?.scrollIntoView?.({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        })
    }, [selectedHashResolved])

    const move = useCallback(
        (delta: number) => {
            const cur = Math.max(
                0,
                visibleItems.findIndex((it) => it.signatureHash === selectedHash)
            )
            const next = Math.min(visibleItems.length - 1, Math.max(0, cur + delta))
            setSelectedHash(visibleItems[next]?.signatureHash)
        },
        [visibleItems, selectedHash]
    )

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault()
                move(-1)
            } else if (e.key === 'ArrowRight') {
                e.preventDefault()
                move(1)
            }
        },
        [move]
    )

    const insert = useCallback(async () => {
        // Defensive: the download button only renders when a look-and-feel is selected.
        /* istanbul ignore next */
        if (!selectedHashResolved) {
            return
        }
        setInserting(true)
        let populatedId: string | undefined
        try {
            const moduleChild = await LookAndFeelService.getItemLookAndFeelPage(
                itemType,
                selectedHashResolved
            )
            // The stored page is the moduleChild; wrap it in an ActivityDTO, hydrate it into the
            // store, then duplicate it into the module so the inserted page and its nested items
            // get fresh ids and the template copy is discarded.
            ModuleService.populateActivities([{ moduleChild }])
            populatedId = moduleChild.id
            const inserted = ActivityEntityService.duplicate(moduleChild.id)
            ModuleEntityService.addActivity(moduleEntityId, inserted.id, pageIndex)
            onInserted?.()
        } catch (e: unknown) {
            console.error('Failed to insert look-and-feel page', e)
            setError('Failed to insert the selected look-and-feel')
        } finally {
            // Discard the hydrated template copy on both success and partial failure so it never
            // leaks into the store.
            if (populatedId) {
                ActivityEntityService.remove(populatedId)
            }
            setInserting(false)
        }
    }, [selectedHashResolved, itemType, moduleEntityId, pageIndex, onInserted])

    if (loading) {
        return (
            <Row padding={24} justifyContent='center'>
                <Spinner />
            </Row>
        )
    }

    return (
        <div style={{ width: 'min(1100px, 88vw)', maxWidth: '100%' }}>
            <Col gridGap={16} width='100%'>
                {error && (
                    <div
                        style={{ color: '#d13212', fontWeight: 600 }}
                        data-test-id='look-and-feel-error'
                    >
                        {error}
                    </div>
                )}

                <Row gridGap={8} alignItems='center'>
                    <View flex={1} />
                    <Button
                        variant={
                            variant === 'template' ? ButtonVariant.Primary : ButtonVariant.Secondary
                        }
                        onClick={() => setVariant('template')}
                    >
                        Template
                    </Button>
                    <Button
                        variant={
                            variant === 'real' ? ButtonVariant.Primary : ButtonVariant.Secondary
                        }
                        onClick={() => setVariant('real')}
                    >
                        Real
                    </Button>
                </Row>

                {visibleItems.length === 0 ? (
                    <Row
                        height={PREVIEW_HEIGHT}
                        alignItems='center'
                        justifyContent='center'
                        backgroundColor='neutral05'
                    >
                        <Text fontSize='T200' color='neutral70'>
                            {`No ${variant} images captured for this item type yet.`}
                        </Text>
                    </Row>
                ) : (
                    <>
                        {/* Large preview of the selected look-and-feel. The hover handlers only
                            toggle the download affordance's visibility (which is also revealed on
                            keyboard focus below), so the container stays presentational. */}
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                        <div
                            onMouseEnter={() => setHoverPreview(true)}
                            onMouseLeave={() => setHoverPreview(false)}
                            data-test-id='look-and-feel-preview'
                            style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: PREVIEW_HEIGHT,
                                background: '#f4f4f4',
                                borderRadius: 8,
                                padding: 12,
                            }}
                        >
                            <img
                                src={selectedUrl}
                                alt={`look-and-feel ${selectedHashResolved ?? ''}`}
                                style={{
                                    maxHeight: '100%',
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                }}
                            />

                            {visibleItems.length > 1 && (
                                <>
                                    <button
                                        type='button'
                                        aria-label='Previous look-and-feel'
                                        data-test-id='look-and-feel-prev'
                                        disabled={selectedIndex === 0}
                                        onClick={() => move(-1)}
                                        style={navStyle('left', selectedIndex === 0)}
                                    >
                                        ‹
                                    </button>
                                    <button
                                        type='button'
                                        aria-label='Next look-and-feel'
                                        data-test-id='look-and-feel-next'
                                        disabled={selectedIndex === visibleItems.length - 1}
                                        onClick={() => move(1)}
                                        style={navStyle(
                                            'right',
                                            selectedIndex === visibleItems.length - 1
                                        )}
                                    >
                                        ›
                                    </button>
                                </>
                            )}

                            {/* Download-into-module action, revealed on hover of the preview. */}
                            <button
                                type='button'
                                data-test-id='use-look-and-feel'
                                aria-label='Use this look-and-feel'
                                title='Add this look-and-feel to your module'
                                disabled={inserting}
                                onMouseEnter={() => setHoverPreview(true)}
                                onMouseLeave={() => setHoverPreview(false)}
                                onFocus={() => setHoverPreview(true)}
                                onBlur={() => setHoverPreview(false)}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    void insert()
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    zIndex: 6,
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: 'rgba(90, 96, 105, 0.5)',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: inserting ? 'default' : 'pointer',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                    opacity: hoverPreview ? (inserting ? INSERTING_OPACITY : 1) : 0,
                                    pointerEvents: hoverPreview ? 'auto' : 'none',
                                    transition: 'opacity 200ms ease',
                                }}
                            >
                                {inserting ? (
                                    <Spinner />
                                ) : (
                                    <svg
                                        width='22'
                                        height='22'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        aria-hidden='true'
                                    >
                                        <path d='M12 3v12' />
                                        <path d='M7 10l5 5 5-5' />
                                        <path d='M5 21h14' />
                                    </svg>
                                )}
                            </button>
                        </div>

                        {/* Horizontally scrollable thumbnail strip. */}
                        <div
                            data-test-id='look-and-feel-strip'
                            ref={stripRef}
                            role='listbox'
                            aria-label='Look and feel thumbnails'
                            tabIndex={0}
                            onKeyDown={onKeyDown}
                            style={{
                                display: 'flex',
                                gap: 12,
                                overflowX: 'auto',
                                padding: '8px 2px',
                                outline: 'none',
                            }}
                        >
                            {visibleItems.map((it) => {
                                const isSelected = it.signatureHash === selectedHashResolved
                                return (
                                    <button
                                        key={it.signatureHash}
                                        ref={isSelected ? selectedThumbRef : undefined}
                                        type='button'
                                        role='option'
                                        aria-selected={isSelected}
                                        aria-label={`look-and-feel ${it.signatureHash}`}
                                        title={it.signatureHash}
                                        data-test-id={`look-and-feel-card-${it.signatureHash}`}
                                        onClick={() => {
                                            setSelectedHash(it.signatureHash)
                                            // Move focus to the strip so the left/right arrow keys
                                            // move the selection, and no focus ring lingers on the
                                            // clicked thumbnail (selection is shown by its border).
                                            stripRef.current?.focus()
                                        }}
                                        style={{
                                            flex: '0 0 auto',
                                            width: THUMB_WIDTH,
                                            height: THUMB_HEIGHT,
                                            padding: 4,
                                            cursor: 'pointer',
                                            background: '#fff',
                                            borderRadius: 8,
                                            border: isSelected
                                                ? '2px solid #0972d3'
                                                : '1px solid #d5dbdb',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <img
                                            src={imageFor(it)}
                                            alt={`look-and-feel ${it.signatureHash}`}
                                            style={{
                                                maxHeight: '100%',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </button>
                                )
                            })}
                        </div>

                        <Row justifyContent='center'>
                            <Text fontSize='T100' color='neutral70'>
                                {`${selectedIndex + 1} of ${visibleItems.length}`}
                                {selectedHashResolved ? ` · ${selectedHashResolved}` : ''}
                            </Text>
                        </Row>
                    </>
                )}
            </Col>
        </div>
    )
}
